/**
 * Утилиты для работы с бинарным форматом точек треков
 * Размер одной точки: 38 байт
 *
 * Формат точки:
 * - course: float64 (8 байт)
 * - timestamp: uint32 (4 байта) - Unix timestamp в секундах
 * - lat: float64 (8 байт)
 * - lat2: uint8 (1 байт) - 'N' или 'S'
 * - lon: float64 (8 байт)
 * - lon2: uint8 (1 байт) - 'E' или 'W'
 * - speed: float64 (8 байт)
 */
export const POINT_SIZE = 38;
/**
 * Десериализация массива точек из бинарного буфера
 */
export function deserializePoints(buffer) {
    if (!buffer || buffer.byteLength === 0) {
        return [];
    }
    const pointCount = Math.floor(buffer.byteLength / POINT_SIZE);
    const points = new Array(pointCount);
    const view = new DataView(buffer);
    for (let i = 0; i < pointCount; i++) {
        let offset = i * POINT_SIZE;
        const course = view.getFloat64(offset, true);
        offset += 8;
        const timestamp = view.getUint32(offset, true);
        offset += 4;
        const lat = view.getFloat64(offset, true);
        offset += 8;
        const lat2 = String.fromCharCode(view.getUint8(offset));
        offset += 1;
        const lon = view.getFloat64(offset, true);
        offset += 8;
        const lon2 = String.fromCharCode(view.getUint8(offset));
        offset += 1;
        const speed = view.getFloat64(offset, true);
        points[i] = {
            course,
            timestamp,
            time: timestamp,
            lat,
            lat2,
            lon,
            lng: lon,
            lon2,
            speed,
        };
    }
    return points;
}
/**
 * Сериализация массива точек в бинарный буфер
 */
export function serializePoints(points) {
    if (!points || points.length === 0) {
        return new ArrayBuffer(0);
    }
    const buffer = new ArrayBuffer(points.length * POINT_SIZE);
    const view = new DataView(buffer);
    for (let i = 0; i < points.length; i++) {
        const point = points[i];
        let offset = i * POINT_SIZE;
        // course (uint16)
        view.setFloat64(offset, point.course || 0, true);
        offset += 8;
        // timestamp (uint32)
        const timestamp = point.time || point.timestamp || 0;
        view.setUint32(offset, timestamp > 9999999999 ? Math.floor(timestamp / 1000) : timestamp, true);
        offset += 4;
        // lat (float64)
        view.setFloat64(offset, point.lat || 0, true);
        offset += 8;
        // lat2 (uint8)
        view.setUint8(offset, (point.lat2 || 'N').charCodeAt(0));
        offset += 1;
        // lon (float64)
        view.setFloat64(offset, point.lon || point.lng || 0, true);
        offset += 8;
        // lon2 (uint8)
        view.setUint8(offset, (point.lon2 || 'E').charCodeAt(0));
        offset += 1;
        // speed (float64)
        view.setFloat64(offset, point.speed || 0, true);
    }
    return buffer;
}
/**
 * Проверка, являются ли данные бинарными
 */
export function isBinaryData(data) {
    return !!(data && data.binary === true && data.data instanceof ArrayBuffer);
}
/**
 * Декодирование base64 строки в ArrayBuffer
 */
export function base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}
/**
 * Кодирование ArrayBuffer в base64 строку
 */
export function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}
/**
 * Получение точек из данных (с автоматической десериализацией если нужно)
 */
export function getPointsFromData(intervalData) {
    if (!intervalData) {
        return [];
    }
    // Бинарный формат (ArrayBuffer)
    if (!Array.isArray(intervalData) && intervalData.binary === true && intervalData.data instanceof ArrayBuffer) {
        return deserializePoints(intervalData.data);
    }
    // Base64 формат (строка)
    if (!Array.isArray(intervalData) && intervalData.binary === true && typeof intervalData.data === 'string') {
        const arrayBuffer = base64ToArrayBuffer(intervalData.data);
        return deserializePoints(arrayBuffer);
    }
    // Старый JSON формат
    if (Array.isArray(intervalData)) {
        return intervalData;
    }
    // Обёрнутый JSON формат
    if (intervalData.data && Array.isArray(intervalData.data)) {
        return intervalData.data;
    }
    return [];
}
